import { Autocomplete, AutocompleteItem, Button, Spinner } from '@heroui/react';
import { useInfiniteScroll } from '@heroui/use-infinite-scroll';
import React, { useMemo, useState } from 'react';

import { useDebounce } from '../../../../../../../hooks';
import {
  useCustomCatalogLookup,
  useCustomCatalogSearch,
} from '../../../hooks/useCustomCatalogs';
import {
  buildPickerOptions,
  filterFixedOptions,
  resolveSelectedLabel,
  type CatalogPickerOption,
} from '../utils/catalogPickerOptions';

export const SEARCH_DEBOUNCE_MS = 300;

interface CustomCatalogPickerProps {
  vendorId: string;
  /** Options pinned above the catalogs, such as "Show all products". */
  fixedOptions: CatalogPickerOption[];
  /** A fixed option key or a catalog id. */
  value: string;
  onChange: (value: string) => void;
  label: string;
  placeholder?: string;
  /** Shown unless the field has something more pressing to say. */
  description?: string;
  errorMessage?: string;
  testId?: string;
}

/**
 * Picks one custom catalog, or one of the caller's fixed options.
 *
 * Searches server-side and shows one page: a distributor can own tens of
 * thousands of custom catalogs, so neither loading them all nor rendering them
 * all is an option. The selected catalog is fetched by id separately, so it
 * shows its name whatever the search currently matches.
 */
export const CustomCatalogPicker: React.FC<CustomCatalogPickerProps> = ({
  vendorId,
  fixedOptions,
  value,
  onChange,
  label,
  placeholder = 'Search catalogs',
  description,
  errorMessage,
  testId,
}) => {
  const selectedFixedOption = fixedOptions.find(
    (option) => option.key === value
  );
  const selectedCatalogId = selectedFixedOption ? null : value;

  const { lookup, retry: retryLookup } = useCustomCatalogLookup(
    selectedCatalogId || null
  );
  const isLoadingSelected = lookup?.status === 'loading';

  const selectedLabel = resolveSelectedLabel({ value, fixedOptions, lookup });

  /**
   * The text in the box: the typed term while searching, the selected label
   * otherwise. Held here because the search runs on it.
   */
  const [query, setQuery] = useState(selectedLabel);

  // Follows the stored value — the form loads, resets after a save, and swaps
  // distributor without this field being touched. Derived during render rather
  // than in an effect so the box never paints a stale label.
  const [lastLabel, setLastLabel] = useState(selectedLabel);
  if (lastLabel !== selectedLabel) {
    setLastLabel(selectedLabel);
    setQuery(selectedLabel);
  }

  // Trimmed before the debounce, not after: trailing whitespace would otherwise
  // restart the timer and count as a new term for a request that is identical.
  // Showing the selected label is not a search for it either — an untouched
  // field should list the first page, not the one catalog already picked.
  const trimmedQuery = query.trim();
  const searchTerm = trimmedQuery === selectedLabel.trim() ? '' : trimmedQuery;
  const debouncedSearch = useDebounce(searchTerm, SEARCH_DEBOUNCE_MS);

  const {
    catalogs,
    hasMore,
    isLoading,
    isLoadingMore,
    errorMessage: searchError,
    loadMore,
  } = useCustomCatalogSearch(vendorId, debouncedSearch);

  const [isOpen, setIsOpen] = useState(false);

  // Only while the list is on screen — the scroll container belongs to the
  // popover and is torn down with it. Scroll position rather than a loader
  // sentinel: the Autocomplete owns its item list and has no slot for one.
  const [, scrollRef] = useInfiniteScroll({
    hasMore,
    isEnabled: isOpen,
    shouldUseLoader: false,
    onLoadMore: loadMore,
  });

  const options = useMemo(
    () =>
      buildPickerOptions({
        fixedOptions: filterFixedOptions(fixedOptions, searchTerm),
        catalogs,
        selectedKey: value,
        selectedLabel,
      }),
    [fixedOptions, searchTerm, catalogs, value, selectedLabel]
  );

  // A failed lookup says nothing about whether the catalog exists, so it gets a
  // retry rather than the deleted-catalog prompt.
  const lookupFailed = lookup?.status === 'failed';
  const hint =
    searchError ??
    (lookup?.status === 'not-found' ? 'Pick another catalog.' : null) ??
    (lookupFailed ? undefined : description);

  return (
    <div className="max-w-md space-y-2">
      <Autocomplete
        label={label}
        aria-label={label}
        placeholder={placeholder}
        data-testid={testId}
        // The input spinner covers a fresh search; a further page shows at the
        // foot of the list instead, where the operator is looking.
        isLoading={isLoading || isLoadingSelected}
        scrollRef={scrollRef}
        // Infinite scroll listens on `scrollRef`, which HeroUI puts on the outer
        // wrapper. Past 50 items it would virtualize the list, and then an inner
        // box scrolls instead: the wrapper moves once, one page loads, and paging
        // stops until the list is reopened. Pages arrive 50 at a time and the
        // operator searches long before the DOM grows large.
        isVirtualized={false}
        listboxProps={{
          // Rendered inside the scroll container rather than as an item, so it
          // stays out of keyboard navigation and selection.
          bottomContent: isLoadingMore ? (
            <div
              role="status"
              data-testid="catalog-picker-loading-more"
              className="flex items-center justify-center gap-2 py-2 text-small text-default-500"
            >
              <Spinner size="sm" color="default" />
              Loading more catalogs…
            </div>
          ) : null,
        }}
        onOpenChange={setIsOpen}
        items={options}
        selectedKey={value}
        inputValue={query}
        onInputChange={setQuery}
        errorMessage={errorMessage ?? searchError ?? undefined}
        isInvalid={Boolean(errorMessage ?? searchError)}
        description={hint}
        // The list is the server's answer for the current term; filtering it
        // again here would hide matches the backend already narrowed.
        defaultFilter={() => true}
        onSelectionChange={(key) => {
          // Clearing the input fires with null; keep the saved value rather than
          // writing one nobody chose.
          if (key === null) return;
          const picked = String(key);
          onChange(picked);
          setQuery(
            options.find((option) => option.key === picked)?.label ?? picked
          );
        }}
      >
        {options.map((option) => (
          <AutocompleteItem key={option.key} textValue={option.label}>
            {option.label}
          </AutocompleteItem>
        ))}
      </Autocomplete>
      {lookupFailed && (
        <div
          role="alert"
          className="flex items-center gap-3 text-small text-default-500"
        >
          <span>
            The selected catalog couldn&apos;t be loaded. It stays selected.
          </span>
          <Button size="sm" variant="flat" onPress={retryLookup}>
            Try again
          </Button>
        </div>
      )}
    </div>
  );
};
